'use client';

import React, { useCallback, useMemo } from 'react';

interface QuestionButtonsProps {
  buttons: string[];
  onButtonClick: (buttonText: string) => void;
  disabled?: boolean;
}

// Individual button component to prevent unnecessary re-renders
interface QuestionButtonProps {
  text: string;
  onClick: (text: string) => void;
  disabled: boolean;
}

const QuestionButton: React.FC<QuestionButtonProps> = React.memo(
  ({ text, onClick, disabled }) => {
    const handleClick = useCallback(() => {
      onClick(text);
    }, [onClick, text]);

    return (
      <button
        onClick={handleClick}
        disabled={disabled}
        type="button"
        aria-disabled={disabled}
        className={`
          px-6 py-2.5 rounded-button text-sm font-medium transition-all duration-200
          ${
            disabled
              ? 'bg-lawmingo-gray/50 text-lawmingo-cream/30 cursor-not-allowed'
              : 'bg-transparent text-accent-600 hover:bg-accent-600 hover:text-lawmingo-charcoal active:bg-accent-700'
          }
          border-2 border-accent-600 hover:shadow-lawmingo
          focus:outline-none focus:ring-2 focus:ring-accent-600 focus:ring-opacity-50
        `}
      >
        {text}
      </button>
    );
  }
);
QuestionButton.displayName = 'QuestionButton';

const QuestionButtons: React.FC<QuestionButtonsProps> = ({
  buttons,
  onButtonClick,
  disabled = false,
}) => {
  // Memoize the stable button keys
  const buttonKeys = useMemo(
    () => buttons.map((btn, idx) => `btn-${idx}-${btn.slice(0, 10)}`),
    [buttons]
  );

  if (!buttons || buttons.length === 0) {
    return null;
  }

  return (
    <div
      className="flex flex-wrap gap-2 mt-3"
      role="group"
      aria-label="Dostepne odpowiedzi"
    >
      {buttons.map((button, index) => (
        <QuestionButton
          key={buttonKeys[index]}
          text={button}
          onClick={onButtonClick}
          disabled={disabled}
        />
      ))}
    </div>
  );
};

export default React.memo(QuestionButtons);
